/**
 * Vap 测试页：验证 MP4 是否可正常播放显示
 * 访问 #/activity/test 点击按钮测试（不依赖 MediaModal，仅用 Vap + 原生 video 回退）
 */
import React, { useState, useRef, useEffect } from 'react'
import Vap from 'video-animation-player'
import { toAppShow } from 'src/helper/webviewFun'

const TEST_MP4_URL = 'https://d36e7ppveph0k7.cloudfront.net/08ea30d946e3585edfd99c0d4919b908.mp4'
const TEST_SVGA_URL = 'https://d36e7ppveph0k7.cloudfront.net/b05e58a84229728cb2b1f6d5c3075bb8.svga'
const TEST_PIC_URL = 'https://d36e7ppveph0k7.cloudfront.net/08ea30d946e3585edfd99c0d4919b908.jpg'

const vapJson = {
  horse: {
    info: {
      v: 2,
      f: 120,
      w: 400,
      h: 750,
      fps: 30,
      videoW: 608,
      videoH: 752,
      aFrame: [404, 0, 200, 375],
      rgbFrame: [0, 0, 400, 750],
      isVapx: 0,
      orien: 0,
    },
  },
  entry: {
    info: {
      v: 2,
      f: 48,
      w: 732,
      h: 189,
      fps: 16,
      videoW: 736,
      videoH: 288,
      aFrame: [0, 193, 366, 94],
      rgbFrame: [0, 0, 732, 189],
      isVapx: 0,
      orien: 0,
    },
  },
  decoration: {
    info: {
      v: 2,
      f: 120,
      w: 750,
      h: 1624,
      fps: 30,
      videoW: 1136,
      videoH: 1632,
      aFrame: [754, 0, 375, 812],
      rgbFrame: [0, 0, 750, 1624],
      isVapx: 0,
      orien: 0,
    },
  },
  gift: {
    info: {
      v: 2,
      f: 180,
      w: 400,
      h: 1000,
      fps: 30,
      videoW: 1136,
      videoH: 1008,
      aFrame: [754, 0, 375, 500],
      rgbFrame: [0, 0, 750, 1000],
      isVapx: 0,
      orien: 0,
    },
  },
}

const DEFAULT_VAP_CONFIG = vapJson.horse

export default function Test() {
  const [visible, setVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [useNative, setUseNative] = useState(false)
  const vapRef = useRef(null)
  const containerRef = useRef(null)

  const close = () => {
    if (vapRef.current) {
      try {
        vapRef.current.pause()
        vapRef.current.destroy()
      } catch (e) {}
      vapRef.current = null
    }
    const video = document.querySelector('#test-video')
    if (video) {
      video.pause()
      video.src = ''
    }
    setVisible(false)
    setLoading(false)
    setUseNative(false)
  }

  useEffect(() => {
    if (!visible || !containerRef.current) return

    setLoading(true)
    setUseNative(false)
    const container = document.querySelector('#test-vap')
    if (!container) {
      setLoading(false)
      return
    }

    let vap = null
    // Vap 内部的 parse error 只会 console.error，不会触发 error 事件
    // 用超时兜底：5秒内没播放则切原生 video
    const fallbackTimer = setTimeout(() => {
      console.warn('[Test] Vap 未在 5s 内开始播放，切换到原生 video 兜底')
      setUseNative(true)
      setLoading(false)
    }, 5000)

    try {
      vap = new Vap({
        container,
        src: TEST_MP4_URL,
        config: DEFAULT_VAP_CONFIG,
        loop: true,
        beginPoint: 0,
        type: 1,
        precache: true,
      })
      vap.on('playing', () => {
        clearTimeout(fallbackTimer)
        setLoading(false)
      })
      vap.on('error', () => {
        clearTimeout(fallbackTimer)
        setUseNative(true)
        setLoading(false)
      })
      vapRef.current = vap
    } catch (e) {
      clearTimeout(fallbackTimer)
      setUseNative(true)
      setLoading(false)
    }

    return () => {
      clearTimeout(fallbackTimer)
      if (vap) {
        try {
          vap.pause()
          vap.destroy()
        } catch (err) {}
      }
      vapRef.current = null
    }
  }, [visible])

  useEffect(() => {
    if (!useNative || !visible) return
    const video = document.querySelector('#test-video')
    if (!video) return
    video.src = TEST_MP4_URL
    video.load()
    const onCanPlay = () => {
      setLoading(false)
      video.play().catch(() => setLoading(false))
    }
    video.addEventListener('canplay', onCanPlay, { once: true })
    video.addEventListener('error', () => setLoading(false), { once: true })
    return () => {
      video.removeEventListener('canplay', onCanPlay)
    }
  }, [useNative, visible])

  return (
    <div style={{ padding: '0.4rem', minHeight: '100vh', background: '#1a1a2e' }}>
      <h2 style={{ color: '#eee', marginBottom: '0.3rem', fontSize: '0.36rem' }}>Vap MP4 测试</h2>
      <p style={{ color: '#999', fontSize: '0.28rem', marginBottom: '0.4rem' }}>
        点击按钮打开弹窗，先尝试 Vap 播放，失败则用原生 video 播放
      </p>
      <button
        type="button"
        onClick={() => setVisible(true)}
        style={{
          padding: '0.2rem 0.4rem',
          fontSize: '0.3rem',
          background: '#4a90d9',
          color: '#fff',
          border: 'none',
          borderRadius: '0.08rem',
        }}
      >
        播放 MP4
      </button>
      <div style={{ width: '100px', height: '20px' }}></div>
      <button
        type="button"
        onClick={() => toAppShow({ loopUrl: TEST_MP4_URL, loop: 1, loopType: 'mp4' })}
        style={{
          padding: '0.2rem 0.4rem',
          fontSize: '0.3rem',
          background: '#4a90d9',
          color: '#fff',
          border: 'none',
          borderRadius: '0.08rem',
        }}
      >
        App播放MP4
      </button>
      <button
        type="button"
        onClick={() => toAppShow({ loopUrl: TEST_SVGA_URL, loop: 1, loopType: 'svga' })}
        style={{
          marginLeft: '0.2rem',
          padding: '0.2rem 0.4rem',
          fontSize: '0.3rem',
          background: '#4a90d9',
          color: '#fff',
          border: 'none',
          borderRadius: '0.08rem',
        }}
      >
        App播放SVGA
      </button>
      <div style={{ width: '100px', height: '20px' }}></div>
      <button
        type="button"
        onClick={() => toAppShow({ loopUrl: TEST_PIC_URL, loop: 0, loopType: 'pic' })}
        style={{
          padding: '0.2rem 0.4rem',
          fontSize: '0.3rem',
          background: '#4a90d9',
          color: '#fff',
          border: 'none',
          borderRadius: '0.08rem',
        }}
      >
        App打开图片
      </button>

      {visible && (
        <div
          style={{
            position: 'fixed',
            zIndex: 9999,
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={close}
        >
          <div
            style={{
              width: '7.5rem',
              height: '100vh',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={close}
              style={{
                position: 'absolute',
                top: '0.2rem',
                right: '0.2rem',
                zIndex: 10,
                width: '0.6rem',
                height: '0.6rem',
                padding: 0,
                border: 'none',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.3)',
                color: '#fff',
                fontSize: '0.36rem',
                lineHeight: 1,
                cursor: 'pointer',
              }}
              aria-label="关闭"
            >
              ×
            </button>
            {useNative ? (
              <video
                id="test-video"
                autoPlay
                loop
                muted
                playsInline
                style={{
                  maxWidth: '100%',
                  maxHeight: '100vh',
                  width: 'auto',
                  height: 'auto',
                  display: 'block',
                  margin: '0 auto',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <div
                id="test-vap"
                ref={containerRef}
                style={{
                  width: '7.5rem',
                  height: '100vh',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                }}
              />
            )}
            {loading && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: '#fff',
                  fontSize: '0.28rem',
                }}
              >
                加载中...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}



// WEBPACK FOOTER //
// src/pages/activity/test/index.js